import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

import { NavigationBar } from '@/components/NavigationBar';
import StepperCard from '@/components/common/StepperCard.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useRunOnce } from '@/hooks/use-run-once';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { identifyUser, trackEligibilityCheckpoint } from '@/lib/analytics';
import { getBaseInsuranceEligibilityEventAttributes } from '@/types/insurance-builder.type';
import {
  type FormData,
  InsuranceWaitlistForm,
} from '@/views/insurance/components/InsuranceWaitlistForm.tsx';

export const InsuranceEligibilityFailReasonSchema = z
  .enum(['zip_fail', 'out_of_network'])
  .optional()
  .catch(undefined);

export type InsuranceEligibilityFailReason = z.infer<typeof InsuranceEligibilityFailReasonSchema>;

export function InsuranceIneligible() {
  const navigate = useNavigate();
  const { reason: failReason } = useSearch({ strict: false });

  const onBack = () => navigate({ to: '/1' });

  useRunOnce(function trackEligibilityCheckpointOnMount() {
    const { insuranceBuilder, insuranceZipCode, wizardVariantId } = useSessionStore.getState();
    const attributes = getBaseInsuranceEligibilityEventAttributes(
      insuranceBuilder,
      insuranceZipCode,
      { usePlanFamilyStatus: wizardVariantId === 'eligibility-revamp' }
    );

    trackEligibilityCheckpoint({
      ...attributes,
      eligibility_fail_reason: failReason,
    });
  });

  const addToWaitlist = useCallback(
    (data: FormData) => {
      if (data.email) {
        identifyUser(data.email, {
          email: data.email,
          phone: data.phoneNumber,
        });
      }

      navigate({ to: '/insurance/waitlist/thank-you' });
    },
    [navigate]
  );

  return (
    <section>
      <NavigationBar onBack={onBack} backLabel="Patient info" stepIndicator="2 of 3" />
      <StepperCard currentStep={2} totalSteps={3}>
        <CardHeader>
          <CardTitle className="text-center">{getHeaderText(failReason)}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-8">
          <p className="text-base text-gray-700">
            We're always expanding and adding new {failReason === 'zip_fail' ? 'regions' : 'plans'}.
            Sign up to be notified and we'll reach out when your plan is in network.
          </p>

          <InsuranceWaitlistForm onSubmit={addToWaitlist} />
        </CardContent>
      </StepperCard>
    </section>
  );
}

const getHeaderText = (failReason: InsuranceEligibilityFailReason) => {
  switch (failReason) {
    case 'zip_fail':
      return 'Sorry, we’re not available in your area';
    case 'out_of_network':
      return 'We’re currently servicing Medicaid & Medicare patients only.';
    default:
      return "Sorry, we couldn't find any eligible plans for you";
  }
};
